import { useCallback, useEffect, useState } from "react";
import { useForm, Controller } from "react-hook-form";

import { useCreateAirline, useUpdateAirline } from "../../hooks/mutation";
import { toast } from "react-toastify";
import { FaAngleLeft, FaAngleRight, FaEdit } from "react-icons/fa";
import { PAGE_SIZE } from "../Utils/constant";
import TextInput from "../common/TextInput";
import { supabase } from "../../supabaseClient";

const AirlineNew = () => {
  const [page, setPage] = useState(1);
  const [isEdit, setIsEdit] = useState(false);
  const [airline, setAirline] = useState(null);
  const [search, setSearch] = useState(null);
  const [airlinesData, setAirlinesData] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [companies, setCompanies] = useState([]);

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: {
      CompanyID: "",
      Code: "",
      Description: "",
      AirlineUser: "",
      HotelUser: "",
      HotelPass: "",
      CrewUser: "",
      CrewPass: "",
      PortalPassReset: "",
      Active: true,
      AirlineLetterCode: "",
      AirlinePass: "",
    },
  });

  useEffect(() => {
    if (airline) {
      reset({
        CompanyID: airline.CompanyID,
        Code: airline.Code,
        Description: airline.Description,
        AirlineUser: airline.AirlineUser,
        HotelUser: airline.HotelUser,
        HotelPass: airline.HotelPass,
        CrewUser: airline.CrewUser,
        CrewPass: airline.CrewPass,
        PortalPassReset: airline.PortalPassReset,
        Active: airline.Active,
        AirlineLetterCode: airline.AirlineLetterCode,
        AirlinePass: airline.AirlinePass,
      });
    }
  }, [airline, reset]);

  useEffect(() => {
    const fetchCompanies = async () => {
      const { data: Companies, error: cError } = await supabase
        .from("Companies")
        .select("CompanyID, Name");
      if (cError) {
        setCompanies([]);
      } else {
        setCompanies(Companies);
        reset((prev) => ({
          ...prev,
          CompanyID: Companies[0] ? Companies[0].CompanyID : "",
        }));
      }
    };
    fetchCompanies();
  }, [reset]);

  useEffect(() => {
    const fetchAirlines = async () => {
      let query = supabase
        .from("Airlines")
        .select(
          `
          AirlineID,
          created_at,
          CompanyID,
          Code,
          Description,
          AirlineUser,
          HotelUser,
          HotelPass,
          CrewUser,
          CrewPass,
          PortalPassReset,
          Active,
          AirlineLetterCode,
          AirlinePass
        `,
          { count: "exact" }
        )
        .order("created_at", { ascending: false })
        .range((page - 1) * PAGE_SIZE, page * PAGE_SIZE - 1);

      // Apply search filters if provided
      if (search && search.text && search.text.trim() !== "") {
        const field = search.type;
        const value = search.text.trim();

        switch (field) {
          case "Code":
            switch (search.which.toLowerCase()) {
              case "equals":
                query = query.eq("Code", value);
                break;
              case "contains":
                query = query.ilike("Code", `%${value}%`);
                break;
              case "starts with":
                query = query.ilike("Code", `${value}%`);
                break;
              case "ends with":
                query = query.ilike("Code", `%${value}`);
                break;
            }
            break;
          case "Description":
            switch (search.which.toLowerCase()) {
              case "equals":
                query = query.eq("Description", value);
                break;
              case "contains":
                query = query.ilike("Description", `%${value}%`);
                break;
              case "starts with":
                query = query.ilike("Description", `${value}%`);
                break;
              case "ends with":
                query = query.ilike("Description", `%${value}`);
                break;
            }
            break;
          case "AirlineLetterCode":
            switch (search.which.toLowerCase()) {
              case "equals":
                query = query.eq("AirlineLetterCode", value);
                break;
              case "contains":
                query = query.ilike("AirlineLetterCode", `%${value}%`);
                break;
              case "starts with":
                query = query.ilike("AirlineLetterCode", `${value}%`);
                break;
              case "ends with":
                query = query.ilike("AirlineLetterCode", `%${value}`);
                break;
            }
            break;
        }
      }

      const { data: Airlines, count, error: aError } = await query;

      console.log("++++++++++++++++", Airlines, aError, count);
      if (aError) {
        setAirlinesData([]);
        setTotalCount(0);
      } else {
        setAirlinesData(Airlines || []);
        setTotalCount(count || 0);
      }
    };
    fetchAirlines();
  }, [page, search]);

  const onNextPage = useCallback(() => {
    setPage((old) => old + 1);
  }, []);
  const onPrevPage = useCallback(() => {
    setPage((old) => old - 1);
  }, []);

  const onSuccess = useCallback((_data) => {
    toast.success("Success");
    setIsEdit(false);
    setAirline(null);
    // Refresh the list
    window.location.reload();
  }, []);
  const onError = useCallback((err) => {
    console.log("error", err);
    toast.error("Error occurred");
  }, []);

  const onAddNew = () => {
    reset({
      CompanyID: companies[0] ? companies[0].CompanyID : "",
      Code: "",
      Description: "",
      AirlineUser: "",
      HotelUser: "",
      HotelPass: "",
      CrewUser: "",
      CrewPass: "",
      PortalPassReset: "",
      Active: true,
      AirlineLetterCode: "",
      AirlinePass: "",
    });
    setIsEdit(true);
  };

  const OnCancel = () => {
    setIsEdit(false);
    setAirline(null);
  };

  const { mutate: createAirline, isPending: isCreating } = useCreateAirline(
    onSuccess,
    onError
  );

  const { mutate: updateAirline, isPending: isUpdating } = useUpdateAirline(
    onSuccess,
    onError
  );

  const OnCreate = useCallback(
    (data) => {
      if (airline) {
        updateAirline({ ...data, ID: airline.AirlineID });
      } else {
        createAirline(data);
      }
    },
    [airline, createAirline, updateAirline]
  );

  const onEdit = (airlineData) => {
    setAirline(airlineData);
    setIsEdit(true);
  };

  const onDelete = async (airlineId) => {
    if (window.confirm("Are you sure you want to delete this airline?")) {
      const { error } = await supabase
        .from("Airlines")
        .delete()
        .eq("AirlineID", airlineId);

      if (error) {
        toast.error("Error deleting airline");
      } else {
        toast.success("Airline deleted successfully");
        // Refresh the list
        window.location.reload();
      }
    }
  };

  return (
    <div>
      <div className="flex">
        <h5 className="mb-0">Airline Management</h5>
        <button
          className="justify-between rounded-lg ml-1 flex items-center custom-blue-button border-none"
          onClick={onAddNew}
        >
          <span>Add New Airline</span>
        </button>
      </div>

      <SearchBar setSearch={setSearch} OnSearch={() => {}} />

      {isEdit && (
        <div className="mt-4 p-4 border rounded-lg bg-gray-50">
          <h6 className="mb-3">Airline Details</h6>
          <form onSubmit={handleSubmit(OnCreate)}>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">
                  Company
                </label>
                <Controller
                  control={control}
                  name="CompanyID"
                  rules={{ required: "Company is required" }}
                  render={({ field: { value, onChange } }) => (
                    <select
                      className="custom-input rounded-lg w-full"
                      value={value}
                      onChange={onChange}
                    >
                      <option value="">Select Company</option>
                      {companies.map((company) => (
                        <option
                          key={company.CompanyID}
                          value={company.CompanyID}
                        >
                          {company.Name}
                        </option>
                      ))}
                    </select>
                  )}
                />
                {errors.CompanyID && (
                  <p className="text-red-500 text-xs mt-1">
                    {errors.CompanyID.message}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Code</label>
                <Controller
                  control={control}
                  name="Code"
                  rules={{ required: "Code is required" }}
                  render={({ field: { value, onChange } }) => (
                    <TextInput
                      value={value}
                      onChange={onChange}
                      placeholder="e.g., AM001"
                      className="custom-input rounded-lg w-full"
                    />
                  )}
                />
                {errors.Code && (
                  <p className="text-red-500 text-xs mt-1">
                    {errors.Code.message}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">
                  Description
                </label>
                <Controller
                  control={control}
                  name="Description"
                  rules={{ required: "Description is required" }}
                  render={({ field: { value, onChange } }) => (
                    <TextInput
                      value={value}
                      onChange={onChange}
                      placeholder="e.g., Aeromexico"
                      className="custom-input rounded-lg w-full"
                    />
                  )}
                />
                {errors.Description && (
                  <p className="text-red-500 text-xs mt-1">
                    {errors.Description.message}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">
                  Airline Letter Code
                </label>
                <Controller
                  control={control}
                  name="AirlineLetterCode"
                  rules={{ required: "Airline Letter Code is required" }}
                  render={({ field: { value, onChange } }) => (
                    <TextInput
                      value={value}
                      onChange={onChange}
                      placeholder="e.g., AM"
                      className="custom-input rounded-lg w-full"
                    />
                  )}
                />
                {errors.AirlineLetterCode && (
                  <p className="text-red-500 text-xs mt-1">
                    {errors.AirlineLetterCode.message}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">
                  Airline User
                </label>
                <Controller
                  control={control}
                  name="AirlineUser"
                  render={({ field: { value, onChange } }) => (
                    <TextInput
                      value={value}
                      onChange={onChange}
                      placeholder="Airline username"
                      className="custom-input rounded-lg w-full"
                    />
                  )}
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">
                  Airline Password
                </label>
                <Controller
                  control={control}
                  name="AirlinePass"
                  render={({ field: { value, onChange } }) => (
                    <TextInput
                      value={value}
                      onChange={onChange}
                      type="password"
                      placeholder="Airline password"
                      className="custom-input rounded-lg w-full"
                    />
                  )}
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">
                  Hotel User
                </label>
                <Controller
                  control={control}
                  name="HotelUser"
                  render={({ field: { value, onChange } }) => (
                    <TextInput
                      value={value}
                      onChange={onChange}
                      placeholder="Hotel username"
                      className="custom-input rounded-lg w-full"
                    />
                  )}
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">
                  Hotel Password
                </label>
                <Controller
                  control={control}
                  name="HotelPass"
                  render={({ field: { value, onChange } }) => (
                    <TextInput
                      value={value}
                      onChange={onChange}
                      type="password"
                      placeholder="Hotel password"
                      className="custom-input rounded-lg w-full"
                    />
                  )}
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">
                  Crew User
                </label>
                <Controller
                  control={control}
                  name="CrewUser"
                  render={({ field: { value, onChange } }) => (
                    <TextInput
                      value={value}
                      onChange={onChange}
                      placeholder="Crew username"
                      className="custom-input rounded-lg w-full"
                    />
                  )}
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">
                  Crew Password
                </label>
                <Controller
                  control={control}
                  name="CrewPass"
                  render={({ field: { value, onChange } }) => (
                    <TextInput
                      value={value}
                      onChange={onChange}
                      type="password"
                      placeholder="Crew password"
                      className="custom-input rounded-lg w-full"
                    />
                  )}
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">
                  Portal Pass Reset
                </label>
                <Controller
                  control={control}
                  name="PortalPassReset"
                  render={({ field: { value, onChange } }) => (
                    <TextInput
                      value={value}
                      onChange={onChange}
                      placeholder="Portal pass reset"
                      className="custom-input rounded-lg w-full"
                    />
                  )}
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Active</label>
                <Controller
                  control={control}
                  name="Active"
                  render={({ field: { value, onChange } }) => (
                    <input
                      type="checkbox"
                      checked={value}
                      onChange={onChange}
                      className="mr-2"
                    />
                  )}
                />
              </div>
            </div>

            <div className="flex justify-end gap-2 mt-4">
              <button
                type="submit"
                disabled={isCreating || isUpdating}
                className="justify-between p-2 pr-20 pl-20 rounded-lg flex items-center custom-blue-button border-none"
              >
                <span>{airline ? "Update" : "Create"}</span>
              </button>
              <button
                type="button"
                onClick={OnCancel}
                className="justify-between pl-20 pr-20 p-2 rounded-lg flex items-center custom-white-button"
              >
                <span>Cancel</span>
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="mt-4">
        <table className="table-auto w-full border border-gray-200">
          <thead>
            <tr className="bg-gray-100">
              <th className="px-4 py-2 border">ID</th>
              <th className="px-4 py-2 border">Code</th>
              <th className="px-4 py-2 border">Description</th>
              <th className="px-4 py-2 border">Letter Code</th>
              <th className="px-4 py-2 border">Company</th>
              <th className="px-4 py-2 border">Active</th>
              <th className="px-4 py-2 border">Actions</th>
            </tr>
          </thead>
          <tbody>
            {airlinesData?.map((airline) => (
              <tr key={airline.AirlineID} className="hover:bg-gray-50">
                <td className="px-4 py-2 border">{airline.AirlineID}</td>
                <td className="px-4 py-2 border">{airline.Code}</td>
                <td className="px-4 py-2 border">{airline.Description}</td>
                <td className="px-4 py-2 border">
                  {airline.AirlineLetterCode}
                </td>
                <td className="px-4 py-2 border">
                  {companies.find((c) => c.CompanyID === airline.CompanyID)
                    ?.Name || "N/A"}
                </td>
                <td className="px-4 py-2 border">
                  {airline.Active ? "Yes" : "No"}
                </td>
                <td className="px-4 py-2 border">
                  <div className="flex gap-2">
                    <button
                      onClick={() => onEdit(airline)}
                      className="p-1 text-blue-600 hover:text-blue-800"
                    >
                      <FaEdit />
                    </button>
                    <button
                      onClick={() => onDelete(airline.AirlineID)}
                      className="p-1 text-red-600 hover:text-red-800"
                    >
                      Delete
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        <div className="pagination-container justify-start mt-4">
          <button
            onClick={onPrevPage}
            disabled={page === 1}
            className="pagination-button"
          >
            <FaAngleLeft className="pagination-icon" />
          </button>

          <span className="mx-4">
            Page {page} of {Math.ceil(totalCount / PAGE_SIZE)}
          </span>

          <button
            onClick={onNextPage}
            disabled={page * PAGE_SIZE >= totalCount}
            className="pagination-button"
          >
            <FaAngleRight className="pagination-icon" />
          </button>
        </div>
      </div>
    </div>
  );
};

const SearchBar = ({ setSearch, OnSearch }) => {
  const {
    control: searchControl,
    handleSubmit: handleSearchSubmit,
    reset: searchReset,
  } = useForm({
    defaultValues: {
      type: "Code",
      which: "contains",
      text: "",
    },
  });

  return (
    <div className="flex mt-4 items-center gap-4 condition-bar">
      <div className="flex items-center">
        <p className="mb-0">Look For:</p>
        <div className="flex justify-between w-10 ml-1">
          <Controller
            control={searchControl}
            name="type"
            render={({ field: { value, onChange } }) => (
              <select
                className="custom-input rounded-lg cursor-pointer"
                value={value}
                onChange={onChange}
              >
                <option value={"AirlineID"}>AirlineID</option>
                <option value={"Code"}>Code</option>
                <option value={"Description"}>Description</option>
                <option value={"AirlineLetterCode"}>Letter Code</option>
              </select>
            )}
          />
        </div>
      </div>

      <div className="flex items-center gap-2">
        <p className="mb-0">Which:</p>
        <Controller
          control={searchControl}
          name="which"
          render={({ field: { value, onChange } }) => (
            <select
              className="custom-input rounded-lg cursor-pointer"
              value={value}
              onChange={onChange}
            >
              <option value={"equals"}>Equals</option>
              <option value={"contains"}>Contains</option>
              <option value={"starts with"}>Starts With</option>
              <option value={"ends with"}>Ends With</option>
            </select>
          )}
        />
      </div>

      <div className="flex items-center gap-2">
        <Controller
          control={searchControl}
          name="text"
          render={({ field: { value, onChange } }) => (
            <input
              type="text"
              className="custom-input rounded-lg"
              value={value}
              onChange={onChange}
              placeholder="Search text"
            />
          )}
        />
      </div>

      <div className="flex gap-2">
        <button
          onClick={handleSearchSubmit((data) => {
            setSearch(data);
            OnSearch();
          })}
          className="justify-between rounded-lg flex items-center custom-search-button border-none"
        >
          <span>Search</span>
        </button>
        <button
          onClick={() => {
            searchReset();
            setSearch(null);
          }}
          className="justify-between rounded-lg flex items-center custom-reset-button"
        >
          <span>Reset</span>
        </button>
      </div>
    </div>
  );
};

export default AirlineNew;
